import { Plus } from "lucide-react";
import * as React from "react";

import { Button } from "@/shared/foundation/primitives/Button";
import { type Updater } from "@/shared/foundation/primitives/Table";
import { usePopupState } from "@/shared/hooks/usePopupState";

import type { FilterDefinition } from "../FilterDefinition";
import { FilterMenu } from "../FilterMenu";
import type { Filter, FilterMatchMode } from "../FilterSchema";

import styles from "./ActiveFilters.module.css";
import { EditableFilterChip } from "./EditableFilterChip";
import { MatchModePicker } from "./MatchModePicker";
import structuredStyles from "./StructuredActiveFilters.module.css";

export interface StructuredActiveFiltersProps {
  /** The structured filter list to render. Narrowed by the parent
   *  `<ActiveFilters>` switch — the variant component never sees
   *  `"none"` or `"roboql"` state. */
  filters: Filter[];
  /** AND/OR match mode. Picker is hidden when `filters.length < 2`. */
  matchMode: FilterMatchMode;
  filterDefinitions: FilterDefinition[];
  onRemove: (index: number) => void;
  /** Called with the filter's index and the updated filter when the user
   * edits a filter. */
  onUpdate: (index: number, filter: Filter) => void;
  onClear: () => void;
  onMatchModeChange: (updater: Updater<FilterMatchMode>) => void;
  /** Called when the `+` popover commits a structured filter. The parent
   *  hook wraps the raw `Filter` into a `FilterMenuOutput` via
   *  `view.commit`. */
  onFilterSelected: (filter: Filter) => void;
  /** When true, the `+` popover surfaces a Metrics tab alongside Properties
   *  (the same shell as the main Filter button, minus RoboQL). */
  metrics?: boolean;
}

/**
 * Chip-row variant rendered when `filters.type === "structured"`. Owns
 * the match-mode picker (2+ chips), the property chips, the inline `+`
 * button, and the `+` popover.
 *
 * The `+` opens the shared `<FilterMenu>` shell limited to the structured
 * modes — Properties, plus Metrics when `metrics` is set — and never RoboQL.
 * The rationale: when the user already has structured chips in view, the `+`
 * is an "add another structured filter" gesture; RoboQL is a wholesale
 * replacement of all filters, so it stays on the main Filter button where
 * that intent is explicit.
 */
export function StructuredActiveFilters({
  filters,
  matchMode,
  filterDefinitions,
  onRemove,
  onUpdate,
  onClear,
  onMatchModeChange,
  onFilterSelected,
  metrics,
}: StructuredActiveFiltersProps) {
  const addPopup = usePopupState();

  const handleAddClick = (event: React.MouseEvent<HTMLElement>) => {
    if (addPopup.isOpen) {
      addPopup.close();
    } else {
      addPopup.open({ anchorEl: event.currentTarget });
    }
  };

  return (
    <div className={styles.bar} role="region" aria-label="Active filters">
      {filters.length >= 2 && (
        <MatchModePicker mode={matchMode} onChange={onMatchModeChange} />
      )}
      {filters.map((filter, i) => {
        // Hydration with a definition list that doesn't include
        // ``filter.field`` (e.g. URL state restoring a filter for a
        // field whose definition has since been removed) still
        // renders an informative chip — ``fieldName`` falls back to
        // the raw field; option-backed values carry their own labels.
        const definition = findDefinition(filterDefinitions, filter);
        // Metric filters carry their own form metadata (the dotted path);
        // property filters need a definition for the edit form's header,
        // comparators, and options — a definition-less chip (the hydration
        // fallback above) keeps its non-interactive body.
        const editable = filter.type === "metric" || definition !== undefined;
        return (
          <EditableFilterChip
            key={`${filter.field}-${i}`}
            filter={filter}
            index={i}
            fieldName={definition?.name ?? filter.field}
            definition={definition}
            editable={editable}
            onRemove={onRemove}
            onUpdate={onUpdate}
          />
        );
      })}
      <button
        type="button"
        className={structuredStyles.add}
        onClick={handleAddClick}
        aria-haspopup="dialog"
        aria-expanded={addPopup.isOpen}
        aria-label="Add filter"
      >
        <Plus />
      </button>
      <FilterMenu
        open={addPopup.isOpen}
        anchorEl={addPopup.anchor}
        onClose={addPopup.close}
        filterDefinitions={filterDefinitions}
        metrics={metrics}
        onCommit={(output) => {
          // The `+` is structured-only (no roboql), so every commit is a
          // structured filter; forward it to the parent's append handler.
          // FilterMenu closes the popover via `onClose` immediately after.
          if (output.type === "structured") {
            onFilterSelected(output.filter);
          }
        }}
      />
      <div className={styles.trailing}>
        <Button variant="text" size="small" color="inherit" onClick={onClear}>
          Clear
        </Button>
      </div>
    </div>
  );
}

/**
 * Resolves the definition backing a committed filter. Matches on both
 * `field` and `type` so a field that changed type between sessions (e.g.
 * a custom field redefined from string to numeric) doesn't hydrate a
 * mismatched form.
 */
function findDefinition(
  filterDefinitions: FilterDefinition[],
  filter: Filter,
): FilterDefinition | undefined {
  return filterDefinitions.find(
    (d) => d.field === filter.field && d.type === filter.type,
  );
}
